import { IGetExportData } from '../components/common/new-table/table-setting.model'
import { PayloadSendEmail, ResultDropdown } from '../models/broadcastEmail.model'
import {
  GetClientListPayload,
  IClientSetup,
  IGetAccountAssigned,
  IGetAccountListResponse,
  IGetClientAssigned,
  IGetClientById,
  IGetClientListName,
} from '../models/clientSetup.model'
import { ApiResponse } from '../models/common.model'
import { IGetUserListResponse } from '../models/userRole.model'
import customAxios from '../utils/api-util/customAxios'
import axiosInstance from '../utils/api-util/utilApi'

const config = { baseURL: process.env.REACT_APP_CLIENT_SETUP_SERVICE_URL || '' }

export const getPayeeMatchParameters = (): Promise<any> => {
  return axiosInstance.get(`/api/clients/payeeMatchParameters`, config)
}

export const getAccountList = (
  clientId: string,
  considerActivationDate?: boolean,
): Promise<IGetAccountListResponse> => {
  const queryParams = considerActivationDate ? `?considerActivationDate=${considerActivationDate}` : ''
  return axiosInstance.get(`/api/clients/${clientId}/accounts${queryParams}`, config)
}

export const getFilteredAccounts = (payload: any): Promise<any> => {
  return axiosInstance.post(`/accounts/filter`, payload, config)
}

export const deleteAccountApi = (accountId: string): Promise<any> => {
  return axiosInstance.delete(`/accounts/${accountId}`, config)
}

export const bulkDeleteAccountApi = (accountIds: Array<string>): Promise<any> => {
  return axiosInstance.delete(`/accounts/bulk-deletion`, {
    data: { ids: accountIds },
    ...config,
  })
}

export const getIssuedCheckFileFormats = (): Promise<any> => {
  return axiosInstance.get(`/api/clients/issuedCheckFileFormats`, config)
}

export const checkClientDuplicates = (clientName: string, clientNumber: string, id?: string): Promise<any> => {
  return axiosInstance.post(`/api/clients/checkDuplicates`, { clientName, clientNumber, id }, config)
}

export const addClientSetup = (data: IClientSetup): Promise<any> => {
  return axiosInstance.put(`/api/clients`, data, config)
}

export const getClient = (id: string): Promise<any> => {
  const url = 'api/clients/' + id
  return axiosInstance.get(url, config)
}

export const getClientListName = (
  considerActivationDate?: boolean,
  includesArchived?: boolean,
): Promise<ApiResponse<IGetClientListName.Response>> => {
  const queryParams = new URLSearchParams()
  if (considerActivationDate !== undefined) {
    queryParams.append('considerActivationDate', String(considerActivationDate))
  }
  if (includesArchived !== undefined) {
    queryParams.append('includesArchived', String(includesArchived))
  }
  const queryString = queryParams.toString()
  const url = `/api/clients/listClientName${queryString ? '?' + queryString : ''}`
  return axiosInstance.get(url, config)
}

export const getClientById = (payload: IGetClientById.Payload): Promise<ApiResponse<IGetClientById.Response>> => {
  return axiosInstance.get(`/api/clients/${payload.clientId}`, config)
}

export const getClientList = (payload: Partial<GetClientListPayload>) => {
  return axiosInstance.post(`/api/clients/filter`, JSON.stringify(payload), config)
}

export const createFileMappingFormats = (formData: any): any => {
  return customAxios('multipart/form-data').post(`/api/FileMappingFormats`, formData, {
    ...config,
  })
}

export const checkDuplicateFileMappingFormats = (formatName: string) => {
  return axiosInstance.get(`/api/FileMappingFormats/duplicate?formatName=${formatName}`, config)
}

export const getFileMappingFormat = (id: string): Promise<any> => {
  const url = 'api/FileMappingFormats/' + id
  return axiosInstance.get(url, config)
}

export const getFileMappingClients = (id: string): Promise<any> => {
  const url = 'api/FileMappingFormats/' + id + '/clients'
  return axiosInstance.get(url, config)
}

export const getListFileMappingFormats = (payload: Partial<IGetExportData>) => {
  return axiosInstance.post(`/api/FileMappingFormats/filter`, JSON.stringify(payload), config)
}

export const deleteFileMappingFormats = (id: string) => {
  return axiosInstance.delete(`/api/FileMappingFormats/${id}`, config)
}

export const getListFileMappingFormatsByClients = (payload: Partial<IGetExportData>) => {
  return axiosInstance.post(`/api/FileMappingFormats/filter/clients`, JSON.stringify(payload), config)
}

export const getClientAssigned = (
  payload: IGetClientAssigned.Payload,
): Promise<ApiResponse<IGetClientAssigned.Response>> => {
  const { isBankUser, userId } = payload
  const url = `/api/clients/users/${userId}/assigned`
  return axiosInstance.get(url, {
    ...config,
    params: {
      isBankUser,
    },
  })
}

export const getAccountAssigned = (
  payload: IGetAccountAssigned.Payload,
): Promise<ApiResponse<IGetAccountAssigned.Response>> => {
  const url = `/accounts/users/${payload.userId}/assigned`
  return axiosInstance.post(
    url,
    JSON.stringify({
      clientIds: payload.clientIds,
      isBankUser: payload.isBankUser,
    }),
    {
      ...config,
    },
  )
}

export const updateFileMappingFormats = (formData: any, id: string): any => {
  return customAxios('multipart/form-data').put(`/api/FileMappingFormats/${id}`, formData, {
    ...config,
  })
}

export const getClientByIdWithoutPermission = (
  payload: IGetClientById.Payload,
): Promise<ApiResponse<IGetClientById.Response>> => {
  return axiosInstance.get(`/api/clients/${payload.clientId}/clientInfo`, config)
}

export const getFullClientByIdWithoutPermission = (
  payload: IGetClientById.Payload,
): Promise<ApiResponse<IGetClientById.Response>> => {
  return axiosInstance.get(`/api/clients/${payload.clientId}/fullClientInfo`, config)
}

export const getUserListByRole = (userRoleId: string): Promise<IGetUserListResponse> => {
  return axiosInstance.get(`/api/UserRole/GetUsersByRole/${userRoleId}`, config)
}

// service for broadcast
export const getClientUserList = (): Promise<ResultDropdown> => {
  return axiosInstance.get(`/api/broadcastEmails/allClientUsers`, config)
}

export const getBankUserList = (): Promise<ResultDropdown> => {
  return axiosInstance.get(`/api/broadcastEmails/allBankUsers`, config)
}
export const getAllClientList = (): Promise<ResultDropdown> => {
  return axiosInstance.get(`/api/broadcastEmails/allClient`, config)
}

export const sendBroadcastEmail = (payload: PayloadSendEmail): Promise<ResultDropdown> => {
  return axiosInstance.post(`/api/broadcastEmails/sendBroadcastEmail`, payload, config)
}
